import React from "react";
import { Col, Row, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { SchoolDashboard } from "../../common/api";
import { LessonImage } from "../../common/LessonImage";
import {
  formatIsoDateAsLocalTimezoneDate,
  formatIsoDateAsLocalTimezoneTime,
} from "./common";
import {
  COLOR_BLACK,
  COLOR_GRAY_D8,
  COLOR_GREEN_BORDER,
  COLOR_RED,
  COLOR_YELLOW_WARNING,
} from "../../common/colors";

export const LessonExecutionAnalyticsTab = ({
  dashboard,
}: {
  dashboard: SchoolDashboard & { is_at_lesson_execution_level: true };
}) => {
  const { t } = useTranslation();
  return (
    <>
      <br />
      <Stack direction="horizontal" gap={3}>
        <div>
          <LessonImage
            style={{ width: "auto", height: "5em", borderRadius: "5px" }}
            thumbSize="large_cover_ios_2x"
            url={dashboard.cover_asset}
            alt="Lesson cover image"
          />
        </div>
        <div>
          <b>{t("dashboard_single_execution_date")}:</b>{" "}
          {formatIsoDateAsLocalTimezoneDate(dashboard.execution_date_iso)}
          <br />
          <b>{t("dashboard_single_execution_time")}:</b>{" "}
          {formatIsoDateAsLocalTimezoneTime(dashboard.execution_date_iso)}
          <br />
          <b>{t("dashboard_single_execution_time_in_lesson")}:</b>{" "}
          {dashboard.execution_length}
          <br />
          <b>{t("dashboard_single_execution_completion")}:</b>{" "}
          <span className={dashboard.is_bad ? "text-danger" : "text-success"}>
            ✓{dashboard.is_bad && "-"}
            {dashboard.is_good && "+"}
          </span>
        </div>
      </Stack>
      <hr />
      {dashboard.flashcards_statistics && (
        <Row className="g-3 g-sm-5 justify-content-center mb-3">
          <Col xs={12} md={4}>
            <div
              className="card"
              style={{
                borderWidth: "2px",
                borderColor: COLOR_GREEN_BORDER,
              }}
            >
              <div className="card-body p-2">
                <b>{t("dashboard_flashpack_summary_easy")}</b>
                <hr
                  className="my-1"
                  style={{
                    color: COLOR_BLACK,
                    opacity: 1,
                  }}
                />
                {dashboard.flashcards_statistics.easy.length > 0 ? (
                  dashboard.flashcards_statistics.easy.map(
                    (flashcard, index) => (
                      <React.Fragment key={index}>
                        {!!index && (
                          <hr
                            className="my-1"
                            style={{
                              color: COLOR_GRAY_D8,
                            }}
                          />
                        )}
                        {flashcard.text}
                      </React.Fragment>
                    )
                  )
                ) : (
                  <span style={{ color: COLOR_GRAY_D8 }}>
                    {t("dashboard_flashpack_summary_no_cards")}
                  </span>
                )}
              </div>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div
              className="card"
              style={{
                borderWidth: "2px",
                borderColor: COLOR_YELLOW_WARNING,
              }}
            >
              <div className="card-body p-2">
                <b>{t("dashboard_flashpack_summary_intermediate")}</b>
                <hr
                  className="my-1"
                  style={{
                    color: COLOR_BLACK,
                    opacity: 1,
                  }}
                />
                {dashboard.flashcards_statistics.intermediate.length > 0 ? (
                  dashboard.flashcards_statistics.intermediate.map(
                    (flashcard, index) => (
                      <React.Fragment key={index}>
                        {!!index && (
                          <hr
                            className="my-1"
                            style={{
                              color: COLOR_GRAY_D8,
                            }}
                          />
                        )}
                        {flashcard.text}
                      </React.Fragment>
                    )
                  )
                ) : (
                  <span style={{ color: COLOR_GRAY_D8 }}>
                    {t("dashboard_flashpack_summary_no_cards")}
                  </span>
                )}
              </div>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div
              className="card"
              style={{
                borderWidth: "2px",
                borderColor: COLOR_RED,
              }}
            >
              <div className="card-body p-2">
                <b>{t("dashboard_flashpack_summary_difficult")}</b>
                <hr
                  className="my-1"
                  style={{
                    color: COLOR_BLACK,
                    opacity: 1,
                  }}
                />
                {dashboard.flashcards_statistics.difficult.length > 0 ? (
                  dashboard.flashcards_statistics.difficult.map(
                    (flashcard, index) => (
                      <React.Fragment key={index}>
                        {!!index && (
                          <hr
                            className="my-1"
                            style={{
                              color: COLOR_GRAY_D8,
                            }}
                          />
                        )}
                        {flashcard.text}
                      </React.Fragment>
                    )
                  )
                ) : (
                  <span style={{ color: COLOR_GRAY_D8 }}>
                    {t("dashboard_flashpack_summary_no_cards")}
                  </span>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}
      {!!dashboard.reasons.length && (
        <Row>
          <Col>
            <span className="fst-italic text-muted">
              {t("dashboard_single_execution_ai_info")}
            </span>
            <br />
            <br />
            {dashboard.reasons.map((reason, index) => (
              <React.Fragment key={index}>
                <span
                  role="img"
                  aria-label={t(
                    reason.positive
                      ? "dashboard_single_execution_label_positive"
                      : reason.negative
                      ? "dashboard_single_execution_label_negative"
                      : "dashboard_single_execution_label_neutral"
                  )}
                >
                  {reason.positive ? "✅" : reason.negative ? "☝️" : "ℹ️"}
                </span>{" "}
                {reason.reason}
                <br />{" "}
              </React.Fragment>
            ))}
          </Col>
        </Row>
      )}
    </>
  );
};
